// ─────────────────────────────────────────────────────────────────────────────
// Export PDF (impression navigateur) — en-tête et pied à l'identité DU TENANT
// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ CE FICHIER PORTAIT L'IDENTITÉ DE L'ÉDITEUR EN DUR (constante `COMPANY` :
//    nom, tagline, RCCM, NIF, CNSS, adresse de GLADIATOR), imprimée en en-tête ET
//    en pied de **9 exports comptables** — bilan, compte de résultat, balance,
//    grand livre, journaux, ratios, flux de trésorerie, budget, rapport IA.
//    Cinq d'entre eux partent au cabinet comptable, au commissaire aux comptes
//    et à l'administration fiscale. Un bilan portant le RCCM et le NIF d'une
//    AUTRE personne morale que celle dont il présente les comptes n'est pas une
//    coquille : c'est un document incohérent avec son propre contenu.
//
// ⚠️ SANS IDENTITÉ, LE DOCUMENT NE SORT PAS (décision Dom, 25/08/2026).
//    « Un export comptable sans identité est un document qui n'engage personne
//      et peut être attribué à n'importe qui. »
//    AUCUN REPLI vers l'éditeur — c'est le repli qui a créé le problème.
//
//    BLOQUANTS (les 3 mentions qui identifient une personne morale en zone
//    OHADA, obligatoires sur un document comptable) :
//        raison sociale · NIF · RCCM
//    NON BLOQUANTS : logo, adresse, téléphone, site web.
//        Un état comptable sans logo reste valable ; sans identifiant fiscal,
//        l'administration ne peut le rattacher à personne.
//    CNSS : NON bloquant ici. Il le deviendra pour les documents SOCIAUX
//        (bordereaux de cotisation, déclarations annuelles) — strate 2.

// Champs exigés, dans l'ordre où on les nomme à l'utilisateur.
const OBLIGATOIRES = [
  { cle: 'nom',  libelle: 'raison sociale' },  // `nom` porte la raison_sociale (cf. /tenant/branding)
  { cle: 'nif',  libelle: 'NIF' },
  { cle: 'rccm', libelle: 'RCCM' },
];

const vide = (v) => v === null || v === undefined || String(v).trim() === '';

/** Retourne la liste des libellés manquants (vide si l'identité est complète). */
export function champsIdentiteManquants(tenant) {
  if (!tenant) return OBLIGATOIRES.map(c => c.libelle);
  return OBLIGATOIRES.filter(c => vide(tenant[c.cle])).map(c => c.libelle);
}

/** « NIF et RCCM » · « raison sociale, NIF et RCCM » */
function enumerer(l) {
  if (l.length === 1) return l[0];
  return `${l.slice(0, -1).join(', ')} et ${l[l.length - 1]}`;
}

const echapper = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Exporte un élément du DOM en PDF via la boîte d'impression.
 * @param {string} title       Intitulé du document (ex. « Bilan comptable »)
 * @param {HTMLElement} contentEl  L'élément à imprimer
 * @param {object} tenant      Identité du tenant (useTenant()). OBLIGATOIRE.
 */
export function exportPagePdf(title, contentEl, tenant) {
  if (!contentEl) return;

  // ── GARDE D'IDENTITÉ — avant toute ouverture de fenêtre ──────────────────
  const manquants = champsIdentiteManquants(tenant);
  if (manquants.length) {
    // LANGAGE MÉTIER, JAMAIS DE NOM DE COLONNE. Le message dit (1) ce qui manque,
    // (2) POURQUOI c'est bloquant — la conséquence légale, pas une règle interne —
    // et (3) OÙ agir. « Un blocage sans explication se lit comme un bug et finit
    // au support. » On dit « votre société » : l'utilisateur n'est pas un tenant.
    const pluriel = manquants.length > 1;
    alert(
      `Export impossible : ${pluriel ? 'les' : 'le'} ${enumerer(manquants)} de votre société ` +
      `${pluriel ? 'ne sont pas renseignés' : "n'est pas renseigné"}.\n\n` +
      `Un état comptable sans ces mentions ne peut être rattaché à aucune entité légale.\n\n` +
      // ⓘ Regle 62 : l EMPLACEMENT de la donnee est une SOURCE, et il reste dit.
      //   C est l ordre qui part — « Renseignez-les … puis relancez l export ».
      `Ces mentions se trouvent dans Paramètres › Société.`
    );
    // Lien direct vers l'écran concerné — l'admin du tenant y édite bien ces
    // champs (PUT /parametres). On ne redirige pas d'office : on n'interrompt
    // pas un travail en cours sans que la personne l'ait choisi.
    if (window.confirm('Ouvrir la page Paramètres maintenant ?')) {
      window.location.assign('/parametres');
    }
    return;                       // le document NE SORT PAS
  }

  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

  const nom  = echapper(tenant.nom);
  const rccm = echapper(tenant.rccm);
  const nif  = echapper(tenant.nif);
  // Facultatifs : présents seulement s'ils existent. Jamais de valeur inventée.
  const sousTitre = vide(tenant.sous_titre) ? '' : echapper(tenant.sous_titre);
  const cnss      = vide(tenant.cnss) ? '' : ` | CNSS ${echapper(tenant.cnss)}`;
  const lieu      = [tenant.adresse, tenant.ville, tenant.pays].filter(v => !vide(v)).map(echapper).join(', ');
  const pied      = [`RCCM ${rccm}`, `NIF ${nif}`].join(' | ') + cnss + (lieu ? ` | ${lieu}` : '');

  const printWin = window.open('', '_blank', 'width=900,height=700');
  // ⓘ Regle 62 : le message ENONCE le fait et sa cause. « Veuillez autoriser les
  //   popups » ordonnait un reglage de navigateur ; la cause suffit a le deduire.
  if (!printWin) { alert('Export PDF non ouvert : les fenetres surgissantes sont bloquees par le navigateur.'); return; }

  printWin.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${echapper(title)} — ${nom}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #222; padding: 30px 40px; font-size: 12px; }
  .pdf-header { text-align: center; border-bottom: 2px solid #8B1A1A; padding-bottom: 12px; margin-bottom: 20px; }
  .pdf-header h1 { font-size: 18px; color: #8B1A1A; letter-spacing: 1px; margin-bottom: 2px; }
  .pdf-header .tagline { font-size: 10px; color: #888; font-style: italic; margin-bottom: 4px; }
  .pdf-header .doc-title { font-size: 14px; font-weight: 700; margin-top: 8px; }
  .pdf-header .doc-date { font-size: 10px; color: #888; margin-top: 2px; }
  .pdf-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 9px; color: #888; padding: 10px 40px; border-top: 1px solid #8B1A1A; }
  .pdf-content { margin-bottom: 60px; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0; }
  th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid #e0e0e0; font-size: 11px; }
  th { background: #f5f5f5; font-weight: 700; font-size: 10px; text-transform: uppercase; color: #555; }
  .stat-card, .content-card { break-inside: avoid; }
  .stats-grid { display: flex; gap: 12px; margin-bottom: 12px; }
  .stat-card { flex: 1; border: 1px solid #e0e0e0; border-radius: 6px; padding: 10px; text-align: center; }
  .stat-card-label { font-size: 10px; color: #888; text-transform: uppercase; }
  .stat-card-value { font-size: 18px; font-weight: 700; margin: 4px 0; }
  .stat-card-sub { font-size: 9px; color: #888; }
  .content-card { border: 1px solid #e0e0e0; border-radius: 6px; padding: 12px; margin-bottom: 12px; }
  .content-card-title { font-size: 13px; font-weight: 700; color: #333; margin-bottom: 8px; }
  .gss-badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; }
  @media print {
    body { padding: 15px 20px; }
    .pdf-footer { position: fixed; bottom: 0; }
    @page { margin: 15mm 10mm 20mm 10mm; }
  }
</style></head><body>
<div class="pdf-header">
  <h1>${nom}</h1>
  ${sousTitre ? `<div class="tagline">${sousTitre}</div>` : ''}
  <div class="doc-title">${echapper(title)}</div>
  <div class="doc-date">Généré le ${dateStr}</div>
</div>
<div class="pdf-content">${contentEl.innerHTML}</div>
<div class="pdf-footer">
  ${nom}<br>
  ${pied}
</div>
</body></html>`);

  printWin.document.close();
  setTimeout(() => {
    printWin.print();
    printWin.close();
  }, 400);
}
